import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";

import { type RootState } from "app";

import { type Filters } from "./types";
import { renameCollectionPath } from "./utils";

const initialState: { pendingWrites: number; filters: Filters; expandedCollections: string[] } = {
    pendingWrites: 0,
    filters: {
        text: "",
        personal: false,
        public: false,
    },
    expandedCollections: [],
};

type State = typeof initialState;

export const bookmarksSlice = createSlice({
    name: "bookmarks",
    initialState: initialState,
    reducers: {
        writeStarted: (state) => {
            state.pendingWrites += 1;
        },
        writeFinished: (state) => {
            state.pendingWrites -= 1;
        },
        setFilters: (state, action: PayloadAction<Partial<State["filters"]>>) => {
            state.filters = {
                ...state.filters,
                ...action.payload,
            };
        },
        expandCollection: (state, action: PayloadAction<string>) => {
            state.expandedCollections = state.expandedCollections.concat(action.payload);
        },
        closeCollection: (state, action: PayloadAction<string>) => {
            state.expandedCollections = state.expandedCollections.filter((collection) => collection !== action.payload);
        },
        renameExpandedCollection: (state, { payload: { from, to } }: PayloadAction<{ from: string; to: string }>) => {
            state.expandedCollections = state.expandedCollections
                .map((collection) => renameCollectionPath(collection, from, to))
                .filter((collection): collection is string => collection !== undefined && collection !== "");
        },
    },
});

export const selectIsSavingBookmark = (state: RootState) => state.bookmarks.pendingWrites > 0;
export const selectBookmarkFilters = (state: RootState) => state.bookmarks.filters;
export const selectExpandedCollections = (state: RootState) => state.bookmarks.expandedCollections;

export const selectIsCollectionExpanded = createSelector(
    [selectExpandedCollections, (_state, collection: string) => collection],
    (collections, collection) => collections.includes(collection),
);

const { actions, reducer } = bookmarksSlice;
export { actions as bookmarksActions, reducer as bookmarksReducer };
